/**
 * Predicados semanticos.
 *
 * A correcao semantica vive ou morre aqui. Cada predicado devolve um
 * `PredicateOutcome` com RESIDUO -- nao um booleano seco -- porque o residuo e o que
 * alimenta credito parcial, feedback util e telemetria de processo.
 *
 * Todas as distancias sao normalizadas pelo comprimento caracteristico, o que torna
 * os predicados INVARIANTES POR SEMELHANCA. Essa propriedade tem um teste dedicado
 * (rotacao + translacao + escala nao podem mudar nenhum veredito) e e o teste de
 * maior valor do repositorio: pega quase todo bug de tolerancia de uma vez so.
 */

import { formatBR } from '../core/num.ts';
import type { PredicateOutcome } from '../schema/evidence.ts';
import { rt } from '../schema/richtext.ts';
import type { GCircle, GLine, GPoint } from './construction.ts';
import { asLineGeom } from './construction.ts';
import { distPointLine } from './primitives.ts';
import type { Tol } from './tolerance.ts';
import { charLengthOf } from './tolerance.ts';
import type { Vec2 } from './vec.ts';
import { angleAbs, cross, dist, dot, normalize, sub, toDeg } from './vec.ts';

export interface Angle {
  readonly a: GPoint;
  readonly vertex: GPoint;
  readonly b: GPoint;
}

function outcome(
  name: string,
  ok: boolean,
  residual: number,
  unit: PredicateOutcome['unit'],
  tolerance: number,
  message: string,
): PredicateOutcome {
  return { name, ok, residual, unit, tolerance, message: rt(message) };
}

/**
 * A semirreta e a bissetriz do angulo?
 *
 * Tres condicoes, e a segunda e a que costuma faltar em implementacoes ingenuas:
 *
 *  1. parte do vertice;
 *  2. APONTA PARA DENTRO do angulo -- sem isso, a bissetriz EXTERNA passa, porque
 *     ela tambem forma angulos iguais com as duas retas suportes;
 *  3. os angulos com os dois lados coincidem dentro da tolerancia.
 */
export function isAngleBisector(ray: GLine, ang: Angle, tol: Tol): PredicateOutcome {
  const L = charLengthOf([ang.a.pos, ang.vertex.pos, ang.b.pos]);

  // (1) origem no vertice
  const dOrigin = dist(ray.p, ang.vertex.pos);
  if (dOrigin / L > tol.distRel) {
    return outcome(
      'isAngleBisector',
      false,
      dOrigin / L,
      'relative',
      tol.distRel,
      'A semirreta não parte do vértice do ângulo.',
    );
  }

  const u = normalize(sub(ang.a.pos, ang.vertex.pos)); // lado 1
  const w = normalize(sub(ang.b.pos, ang.vertex.pos)); // lado 2
  const d = normalize(ray.dir);

  // A direcao pode estar guardada invertida (uma reta nao tem sentido preferencial).
  // Testa os dois sentidos e fica com o melhor -- so faz sentido para 'line'.
  const candidates: Vec2[] = ray.span === 'line' ? [d, { x: -d.x, y: -d.y }] : [d];

  let best: { dir: Vec2; diffDeg: number; inside: boolean } | null = null;
  for (const c of candidates) {
    const angA = angleAbs(c, u);
    const angB = angleAbs(c, w);
    const diffDeg = toDeg(Math.abs(angA - angB));
    // (2) para dentro: os produtos vetoriais contra os dois lados tem sinais opostos
    const inside = cross(u, c) * cross(w, c) < 0 && dot(c, u) + dot(c, w) > 0;
    if (!best || diffDeg < best.diffDeg || (inside && !best.inside)) {
      best = { dir: c, diffDeg, inside };
    }
  }

  if (!best) {
    return outcome('isAngleBisector', false, Infinity, 'deg', tol.angleDeg, 'Semirreta inválida.');
  }

  if (!best.inside) {
    return outcome(
      'isAngleBisector',
      false,
      best.diffDeg,
      'deg',
      tol.angleDeg,
      'A semirreta não entra na região do ângulo (pode ser a bissetriz externa).',
    );
  }

  // (3) angulos iguais
  const ok = best.diffDeg <= tol.angleDeg;
  return outcome(
    'isAngleBisector',
    ok,
    best.diffDeg,
    'deg',
    tol.angleDeg,
    ok
      ? 'A semirreta divide o ângulo em duas partes iguais.'
      : `Os dois ângulos que ela forma com os lados diferem em ${formatBR(best.diffDeg, 1)}°.`,
  );
}

/** As duas retas sao perpendiculares? */
export function isPerpendicular(l1: GLine, l2: GLine, tol: Tol): PredicateOutcome {
  const a = toDeg(angleAbs(l1.dir, l2.dir));
  const diff = Math.abs(a - 90);
  const ok = diff <= tol.angleDeg;
  return outcome(
    'isPerpendicular',
    ok,
    diff,
    'deg',
    tol.angleDeg,
    ok ? 'As retas são perpendiculares.' : `O ângulo entre elas é de ${formatBR(a, 1)}°.`,
  );
}

/** As duas retas sao paralelas? */
export function isParallel(l1: GLine, l2: GLine, tol: Tol): PredicateOutcome {
  let a = toDeg(angleAbs(l1.dir, l2.dir));
  if (a > 90) a = 180 - a;
  const ok = a <= tol.angleDeg;
  return outcome(
    'isParallel',
    ok,
    a,
    'deg',
    tol.angleDeg,
    ok ? 'As retas são paralelas.' : `Elas formam um ângulo de ${formatBR(a, 1)}°.`,
  );
}

/** O ponto e o ponto medio de AB? */
export function isMidpoint(p: GPoint, a: GPoint, b: GPoint, tol: Tol): PredicateOutcome {
  const L = charLengthOf([a.pos, b.pos]);
  const m = { x: (a.pos.x + b.pos.x) / 2, y: (a.pos.y + b.pos.y) / 2 };
  const rel = dist(p.pos, m) / L;
  const ok = rel <= tol.distRel;
  return outcome(
    'isMidpoint',
    ok,
    rel,
    'relative',
    tol.distRel,
    ok ? 'É o ponto médio.' : 'Não é o ponto médio do segmento.',
  );
}

/** A reta e a mediatriz de AB? */
export function isPerpBisector(l: GLine, a: GPoint, b: GPoint, tol: Tol): PredicateOutcome {
  const L = charLengthOf([a.pos, b.pos]);
  const m = { x: (a.pos.x + b.pos.x) / 2, y: (a.pos.y + b.pos.y) / 2 };
  const dM = distPointLine(m, asLineGeom(l)) / L;

  const ab = normalize(sub(b.pos, a.pos));
  let angDeg = toDeg(angleAbs(l.dir, ab));
  if (angDeg > 90) angDeg = 180 - angDeg;
  const perpErr = Math.abs(angDeg - 90);

  const ok = dM <= tol.distRel && perpErr <= tol.angleDeg;
  return outcome(
    'isPerpBisector',
    ok,
    Math.max(dM, perpErr / 90),
    'relative',
    tol.distRel,
    ok
      ? 'É a mediatriz de AB.'
      : 'Não é a mediatriz: ou não passa pelo ponto médio, ou não é perpendicular ao segmento.',
  );
}

/** O ponto pertence a reta (ou circulo)? */
export function isOnObject(
  p: GPoint,
  host: GLine | GCircle,
  tol: Tol,
  charLength?: number,
): PredicateOutcome {
  const L =
    charLength ??
    (host.kind === 'circle' ? Math.max(host.r, 1e-9) : charLengthOf([host.p, host.q]));
  const d =
    host.kind === 'circle'
      ? Math.abs(dist(p.pos, host.c) - host.r)
      : distPointLine(p.pos, asLineGeom(host));
  const rel = d / L;
  const ok = rel <= tol.distRel;
  return outcome(
    'isOnObject',
    ok,
    rel,
    'relative',
    tol.distRel,
    ok ? 'O ponto está sobre o objeto.' : 'O ponto não está sobre o objeto.',
  );
}

/**
 * O conjunto de pontos marcados coincide com o esperado?
 * Comparacao como CONJUNTO: a ordem em que o aluno marcou nao importa.
 */
export function latticeSetEquals(
  placed: readonly GPoint[],
  expected: readonly Vec2[],
  tol: Tol,
): PredicateOutcome {
  const L = charLengthOf(expected.length >= 2 ? expected : [...expected, { x: 0, y: 0 }]);
  const absTol = tol.distRel * L;

  const usados = new Set<number>();
  let acertos = 0;

  for (const p of placed) {
    for (let i = 0; i < expected.length; i++) {
      if (usados.has(i)) continue;
      if (dist(p.pos, expected[i] as Vec2) <= absTol) {
        usados.add(i);
        acertos++;
        break;
      }
    }
  }

  const faltando = expected.length - acertos;
  const sobrando = placed.length - acertos;
  const erro = faltando + sobrando;
  const ok = erro === 0;

  return outcome(
    'latticeSetEquals',
    ok,
    erro,
    'count',
    0,
    ok
      ? 'Todos os pontos pedidos foram marcados, e nenhum a mais.'
      : `Faltam ${faltando} ponto(s) e sobram ${sobrando}.`,
  );
}

/** Os quatro pontos formam um trapezio retangulo (na ordem dada)? */
export function isRightTrapezoid(
  pts: readonly [GPoint, GPoint, GPoint, GPoint],
  tol: Tol,
): PredicateOutcome {
  const p = pts.map((x) => x.pos);
  const lados: Vec2[] = [];
  for (let i = 0; i < 4; i++) {
    lados.push(sub(p[(i + 1) % 4] as Vec2, p[i] as Vec2));
  }

  // exatamente um par de lados opostos paralelos
  const par1 = anguloEntreRetas(lados[0] as Vec2, lados[2] as Vec2);
  const par2 = anguloEntreRetas(lados[1] as Vec2, lados[3] as Vec2);
  const paralelos = [par1 <= tol.angleDeg, par2 <= tol.angleDeg];
  const nParalelos = paralelos.filter(Boolean).length;

  if (nParalelos !== 1) {
    return outcome(
      'isRightTrapezoid',
      false,
      Math.min(par1, par2),
      'deg',
      tol.angleDeg,
      nParalelos === 0
        ? 'Nenhum par de lados opostos é paralelo.'
        : 'É um paralelogramo, não um trapézio.',
    );
  }

  // Quadrilatero de verdade: nessa ordem, os lados nao podem se cruzar. Sem isso,
  // quatro pontos "fora de ordem" com dois angulos de 90 graus passavam.
  const cruza = (a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean => {
    const o = (u: Vec2, v: Vec2, w: Vec2): number =>
      (v.x - u.x) * (w.y - u.y) - (v.y - u.y) * (w.x - u.x);
    return o(a, b, c) * o(a, b, d) < 0 && o(c, d, a) * o(c, d, b) < 0;
  };
  const [p0, p1, p2, p3] = p as [Vec2, Vec2, Vec2, Vec2];
  if (cruza(p0, p1, p2, p3) || cruza(p1, p2, p3, p0)) {
    return outcome(
      'isRightTrapezoid',
      false,
      90,
      'deg',
      tol.angleDeg,
      'Nessa ordem, os lados se cruzam: os quatro pontos não formam um quadrilátero.',
    );
  }

  // pelo menos dois angulos retos
  let retos = 0;
  let piorErro = 90;
  for (let i = 0; i < 4; i++) {
    const u = sub(p[(i + 1) % 4] as Vec2, p[i] as Vec2);
    const w = sub(p[(i + 3) % 4] as Vec2, p[i] as Vec2);
    const err = Math.abs(toDeg(angleAbs(u, w)) - 90);
    if (err <= tol.angleDeg) retos++;
    else piorErro = Math.min(piorErro, err);
  }

  const ok = retos >= 2;
  return outcome(
    'isRightTrapezoid',
    ok,
    ok ? 0 : piorErro,
    'deg',
    tol.angleDeg,
    ok
      ? 'É um trapézio retângulo.'
      : `Tem ${retos} ângulo(s) reto(s); um trapézio retângulo precisa de 2.`,
  );
}

function anguloEntreRetas(u: Vec2, w: Vec2): number {
  let a = toDeg(angleAbs(u, w));
  if (a > 90) a = 180 - a;
  return a;
}

/** Dois conjuntos de pontos sao congruentes (a menos de isometria)? */
export function isCongruentTo(
  a: readonly GPoint[],
  b: readonly GPoint[],
  tol: Tol,
): PredicateOutcome {
  if (a.length !== b.length) {
    return outcome(
      'isCongruentTo',
      false,
      Math.abs(a.length - b.length),
      'count',
      0,
      'As figuras têm números diferentes de vértices.',
    );
  }
  const L = charLengthOf(a.map((p) => p.pos));
  let pior = 0;
  for (let i = 0; i < a.length; i++) {
    for (let j = i + 1; j < a.length; j++) {
      const da = dist((a[i] as GPoint).pos, (a[j] as GPoint).pos);
      const db = dist((b[i] as GPoint).pos, (b[j] as GPoint).pos);
      pior = Math.max(pior, Math.abs(da - db) / L);
    }
  }
  const ok = pior <= tol.distRel;
  return outcome(
    'isCongruentTo',
    ok,
    pior,
    'relative',
    tol.distRel,
    ok ? 'As figuras são congruentes.' : 'As figuras não são congruentes.',
  );
}
